<template>
  <Modal title="" :max-width="620" :header-border="false" body-overflow="auto" body-max-height="500px" :body-padding="false" @close="onClose">
    <template #header>
      <div class="cd__title-wrap">
        <span class="cd-source">
          <span class="cd-source__mark">
            <AppIcon name="phone_call" :size="11" />
          </span>
          Dialer
        </span>
        <div class="cd__name">{{ titleDisplay }}</div>
        <div class="cd__sub">
          <template v-for="(seg, i) in subSegments" :key="i">
            <span v-if="i > 0" class="cd__dot" />
            <span>{{ seg }}</span>
          </template>
        </div>
      </div>
    </template>

    <div class="eng-body">
      <div class="cd-notes">
        <div v-if="repName" class="cd-note">
          <span class="cd-note__k">Called by</span>
          <span class="cd-note__v">{{ repName }}</span>
        </div>
        <div v-if="data.to_e164" class="cd-note">
          <span class="cd-note__k">Number</span>
          <span class="cd-note__v">{{ data.to_e164 }}</span>
        </div>
        <div class="cd-note">
          <span class="cd-note__k">Duration</span>
          <span class="cd-note__v">{{ durationDisplay }}</span>
        </div>
        <div v-if="data.disposition_code" class="cd-note">
          <span class="cd-note__k">Disposition</span>
          <span class="cd-note__v">{{ data.disposition_label || data.disposition_code }}</span>
        </div>
      </div>

      <div v-if="data.notes" class="eng-prose">{{ data.notes }}</div>
      <div v-else class="eng-empty">No notes were recorded on this call.</div>
    </div>

    <template #footer>
      <button class="cd-btn cd-btn--ghost" @click="onClose">Close</button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import Modal from '../../next/components/Modal.vue'
import AppIcon from '../../components/AppIcon.vue'

/**
 * Detail for a human-dialer call (`call_logs`).
 *
 * Not EngagementDetailDialog: that one is branded per CRM (HubSpot / Pipedrive)
 * and reads its fields from a CRM engagement payload. A Raven dial is our own
 * record, with a disposition vocabulary and rep attribution the CRM shape has
 * nowhere to put.
 */

const props = defineProps<{
  data: Record<string, any>
  createdAt?: string
  onClose: () => void
}>()

const repName = computed(() => String(props.data?.rep_name || '').trim())

const titleDisplay = computed(() =>
  String(props.data?.disposition_label || props.data?.disposition_code || 'Call').trim())

const durationDisplay = computed(() => {
  const total = Number(props.data?.duration_seconds)
  if (!Number.isFinite(total) || total < 0) return '—'
  const mins = Math.floor(total / 60)
  const secs = total % 60
  return mins ? `${mins}m ${secs}s` : `${secs}s`
})

const subSegments = computed(() => {
  const parts: string[] = ['Call']
  const raw = props.data?.started_at || props.createdAt
  if (raw) {
    const dt = new Date(raw)
    if (!Number.isNaN(dt.getTime())) {
      parts.push(dt.toLocaleString([], {
        month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
      }))
    }
  }
  return parts
})
</script>

<style scoped>
.cd__title-wrap { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.cd-source {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.cd-source__mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  background: var(--color-scheduled, #0ea5e9);
  color: #fff;
}
.cd__name { font-size: var(--fs-lg); font-weight: 700; color: var(--color-text-primary); }
.cd__sub {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--color-text-tertiary);
}
.cd__dot {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: var(--color-text-tertiary);
  flex-shrink: 0;
}

.eng-body { padding: var(--space-4); display: flex; flex-direction: column; gap: var(--space-4); }
.eng-prose {
  white-space: pre-wrap;
  font-size: var(--fs-sm);
  line-height: 1.55;
  color: var(--color-text-primary);
  padding-top: var(--space-3);
  border-top: 1px solid var(--color-border-subtle);
}
.eng-empty {
  font-size: var(--fs-sm);
  color: var(--color-text-tertiary);
  padding-top: var(--space-3);
  border-top: 1px solid var(--color-border-subtle);
}
.cd-btn {
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-bg-surface);
  font-family: inherit;
  font-size: var(--fs-sm);
  color: var(--color-text-primary);
  cursor: pointer;
}
.cd-btn--ghost:hover { background: var(--color-bg-subtle); }
</style>
